<template>
  <div class="settings-loc-container m-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Levels Of Care</h5>
      <b-button
        class="pt-2 pb-2"
        size="sm"
        @click="addLevelOfCare"
        variant="primary"
        >New</b-button
      >
    </div>
    <grid
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="levelsOfCare"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
    ></grid>
    <b-modal
      centered
      ref="form-modal"
      :title="formId ? 'Edit Level Of Care' : 'Add Level Of Care'"
      header-bg-variant="primary"
      header-text-variant="white"
      @ok="submitLevelOfCare"
      no-close-on-backdrop
      size="md-lg"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <div class="d-flex justify-content-between align-items-center">
        <b-form-group
          label="Name"
          class="w-50"
          :state="formErrors.name.length == 0 ? null : false"
          :invalid-feedback="formErrors.name.join(', ')"
        >
          <b-form-input
            v-model="form.name"
            maxLength="100"
            :state="formErrors.name.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
        <b-form-group label="Color" class="w-25">
          <color-picker v-model="form.color" />
        </b-form-group>
        <b-form-group label="Order">
          <b-form-input
            v-model.number="form.position"
            type="number"
            placeholder="0"
            min="0"
            max="1000000000"
            @input="validatePositionValue"
          ></b-form-input>
        </b-form-group>
      </div>
      <b-form-group style="margin-bottom:35px;">
        <span class="text-danger" v-if="formErrors.billingCode.length > 0">
          {{ formErrors.billingCode[0] }}</span
        >
        <table-crud
          :fields="billigTableFields"
          title="Billing Codes"
          v-model="form.billingCodes"
        />
      </b-form-group>
      <b-form-group>
        <b-form-checkbox v-model="form.billable" @change="clearSessionHours">
          <label>Billable Service</label>
        </b-form-checkbox>
      </b-form-group>
      <b-form-group
        label="Billable group session hours required for billing:"
        style="margin-bottom:35px;"
      >
        <b-form-input
          v-model.number="form.groupSessionsHoursRequiredForBilling"
          type="number"
          style="width: 70px; height: 100%"
          min="0"
          placeholder="0"
          :disabled="!form.billable"
          @input="validateGroupSessionsHoursRequiredForBilling"
        />
      </b-form-group>
      <b-form-group label="Related documents">
        <multiple-select :options="formOptions" v-model="form.formIds" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import ColorPicker from "../controls/ColorPicker.vue";
import serviceServer from "../../server/service";
import { mapActions, mapState } from "vuex";
import { validateRequired } from "../../helpers/validation";
import TableCrud from "../controls/TableCrud";
import MultipleSelect from "../controls/MultipleSelect";
import billingServer from "../../server/billing";

export default {
  name: "EocLevelOfCareCrud",
  components: {
    Grid,
    ColorPicker,
    TableCrud,
    MultipleSelect
  },
  data() {
    return {
      formId: undefined,
      form: {
        name: "",
        color: "",
        position: 0,
        type: "LevelOfCare",
        groupSessionsHoursRequiredForBilling: 0,
        billingCodes: [],
        formIds: [],
        billable: true
      },
      formErrors: {
        name: [],
        billingCode: [],
        modifiers: []
      },
      codes: []
    };
  },
  mounted() {
    this.fetchLevelsOfCare();
    this.fetchFormBuilderForms({ category: "main" });
    this.form.billable = true;
    this.fetchCodes();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", ["fetchLevelsOfCare"]),
    ...mapActions("formBuilder", ["fetchFormBuilderForms"]),
    validateLevelOfCareSubmit() {
      let withoutErrors = true;

      this.formErrors.name = [];
      if (!validateRequired(this.form.name)) {
        this.formErrors.name.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.billingCode = [];
      for (let index = 0; index < this.form.billingCodes.length; index++) {
        const billingCode = this.form.billingCodes[index];

        if (!validateRequired(billingCode.cptCode)) {
          this.formErrors.billingCode.push(`Insert code`);
          withoutErrors = false;
        }

        if (!validateRequired(billingCode.costPerUnit)) {
          this.formErrors.billingCode.push(
            `Insert cost per unit in ${billingCode.cptCode}`
          );
          withoutErrors = false;
        }

        if (!billingCode.hcpcsCode) {
          this.formErrors.billingCode.push(
            `Insert HCPS Code on: ${billingCode.cptCode}`
          );
          withoutErrors = false;
        }

        if (!billingCode.revenueCode) {
          this.formErrors.billingCode.push(
            `Insert Revenue Code on: ${billingCode.cptCode}`
          );
          withoutErrors = false;
        }

        if (billingCode.mod1 && billingCode.mod1.length > 20) {
          this.formErrors.billingCode.push(
            "The length of Modifier 1 must be 20 characters or fewer"
          );
          withoutErrors = false;
        }

        if (billingCode.mod2 && billingCode.mod2.length > 20) {
          this.formErrors.billingCode.push(
            "The length of Modifier 2 must be 20 characters or fewer"
          );
          withoutErrors = false;
        }

        if (billingCode.mod3 && billingCode.mod3.length > 20) {
          this.formErrors.billingCode.push(
            "The length of Modifier 3 must be 20 characters or fewer"
          );
          withoutErrors = false;
        }

        if (billingCode.mod4 && billingCode.mod4.length > 20) {
          this.formErrors.billingCode.push(
            "The length of Modifier 4 must be 20 characters or fewer"
          );
          withoutErrors = false;
        }

        if (billingCode.costPerUnit > 0) {
          this.form.billingCodes[index].costPerUnit = parseFloat(
            billingCode.costPerUnit
          );
        }
      }
      return withoutErrors;
    },
    validateGroupSessionsHoursRequiredForBilling(){
      this.form.groupSessionsHoursRequiredForBilling = Math.ceil(this.form.groupSessionsHoursRequiredForBilling);
    },
    validatePositionValue(){
      this.form.position = this.form.position > 1000000000 ?  100000000 :  this.form.position;
    },
    async submitLevelOfCare(modalEvent) {
      modalEvent.preventDefault();
      this.form.position = this.form.position || 0;
      const result = this.validateLevelOfCareSubmit();
      if (result) {
        const formData = {
          ...this.form,
          formIds: this.form.formIds.map(form => form.value),
          costPerUnit: this.form.costPerUnit
            ? parseFloat(this.form.costPerUnit)
            : 0
        };
        if (this.formId) {
          await serviceServer.update(this.formId, formData);
        } else {
          await serviceServer.create(formData);
        }
        this.fetchLevelsOfCare();
        this.showAlertSuccess();
        this.$refs["form-modal"].hide();
      }
    },
    addLevelOfCare() {
      this.formId = undefined;
      this.form = {
        name: "",
        color: "#16aaff",
        position: 0,
        type: "LevelOfCare",
        billingCodes: [],
        formIds: [],
        billable: true
      };
      this.formErrors = {
        name: [],
        position: [],
        billingCode: [],
        modifiers: []
      };
      this.$refs["form-modal"].show();
    },
    isLetterOrNumber(e) {
      let char = String.fromCharCode(e.keyCode);
      if (/[a-zA-Z0-9-]/.test(char)) return true;
      else e.preventDefault();
    },
    fetchCodes() {
      billingServer.getUb04Codes().then(data => {
        this.codes = data.revenueCodes;
      });
    },
    clearSessionHours(checked) {
      if (!checked) this.form.groupSessionsHoursRequiredForBilling = 0;
    }
  },
  computed: {
    ...mapState("client", {
      levelsOfCare: state => state.levelsOfCare
    }),
    ...mapState("formBuilder", {
      formOptions: state =>
        [...state.list["main"], ...state.list["masterTreatmentPlan"]]
          .map(f => ({
            value: f.id,
            label: f.name,
            data: f
          }))
          .sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
          )
    }),
    columns() {
      let columns = [
        {
          text: "Name",
          field: "name",
          width: 300,
          sortable(a, b) {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          }
        },
        {
          text: "Color",
          field: "color",
          type: "template",
          width: 70,
          align: "center",
          template: data => {
            return `<div class="d-flex w-100 justify-content-center"><div class="small-square" style="background-color:${
              data.record.data.color ? data.record.data.color : "black"
            };"></div></div>`;
          },
          sortable: false
        },
        {
          text: "Order",
          field: "position",
          width: 100,
          align: "center"
        },
        {
          text: "Billable Group session hours required for billing",
          field: "groupSessionsHoursRequiredForBilling",
          width: 200,
          align: "center"
        },
        {
          text: "Linked documents",
          field: "formIds",
          type: "template",
          flex: 1,
          template: data =>
            data.record.data.formIds.map(f => f.name).join(", "),
          sortable: false
        },
        {
          text: "",
          type: "action",
          field: "action",
          align: "right",
          width: 110,
          actions: [
            {
              tooltip: "Edit",
              renderer: () =>
                `<img class="b-action-item" src="${
                  this.isStandalone()
                    ? `/icons/edit.svg`
                    : `/desktop/icons/edit.svg`
                }">`,
              onClick: async ({ record }) => {
                this.formId = record.id;
                this.form = {
                  ...JSON.parse(JSON.stringify(record.data)),
                  formIds: record.data.formIds.map(form => ({
                    value: form.id,
                    label: form.name,
                    data: form
                  }))
                };
                this.formErrors = {
                  name: [],
                  position: [],
                  billingCode: [],
                  modifiers: []
                };
                this.$refs["form-modal"].show();
              }
            },
            {
              tooltip: "Delete",
              renderer: () =>
                `<img class="b-action-item ml-2 mr-1" src=	"${
                  this.isStandalone()
                    ? `/icons/delete.svg`
                    : `/desktop/icons/delete.svg`
                }">`,
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(async () => {
                    await serviceServer.delete(record.id);
                    this.fetchLevelsOfCare();
                    this.showAlertSuccess();
                  })
                  .catch(function() {});
              }
            }
          ]
        }
      ];
      return columns;
    },
    billigTableFields() {
      return [
        {
          field: "cptCode",
          text: "Cpt Code",
          type: "text"
        },
        {
          field: "revenueCode",
          text: "Revenue Code",
          type: "text_autopopulate",
          options: this.codes
        },
        {
          field: "hcpcsCode",
          text: "Hcpcs Code",
          type: "text"
        },
        {
          field: "mod1",
          text: "Mod 1",
          type: "text"
        },
        {
          field: "mod2",
          text: "Mod 2",
          type: "text"
        },
        {
          field: "mod3",
          text: "Mod 3",
          type: "text"
        },
        {
          field: "mod4",
          text: "Mod 4",
          type: "text"
        },
        {
          field: "costPerUnit",
          text: "Cost per unit ($)",
          type: "number"
        }
      ];
    }
  }
};
</script>

<style>
.settings-loc-container .b-grid-container .b-container {
  max-height: calc(100vh - 281px);
}
</style>
